import React, { useEffect, useState, useRef } from 'react';
import { Bell, X, CheckCheck, Trash2, Bot, AlertTriangle, Zap, GitPullRequest, CheckCircle2, Rocket, Plug, AlertCircle, Clock, Info } from 'lucide-react';
import { useNotifications, Notification, NotificationType } from '../../hooks/useNotifications';
const typeConfig: Record<NotificationType, {
  icon: React.ElementType;
  color: string;
  bg: string;
  dot: string;
}> = {
  agent_interaction: {
    icon: Bot,
    color: 'text-indigo-400',
    bg: 'bg-indigo-950/50 border-indigo-900/40',
    dot: 'bg-indigo-400'
  },
  blocker: {
    icon: AlertTriangle,
    color: 'text-red-400',
    bg: 'bg-red-950/50 border-red-900/40',
    dot: 'bg-red-400'
  },
  sprint_alert: {
    icon: Zap,
    color: 'text-amber-400',
    bg: 'bg-amber-950/50 border-amber-900/40',
    dot: 'bg-amber-400'
  },
  issue_update: {
    icon: CheckCircle2,
    color: 'text-emerald-400',
    bg: 'bg-emerald-950/50 border-emerald-900/40',
    dot: 'bg-emerald-400'
  },
  deploy: {
    icon: Rocket,
    color: 'text-cyan-400',
    bg: 'bg-cyan-950/50 border-cyan-900/40',
    dot: 'bg-cyan-400'
  },
  pr: {
    icon: GitPullRequest,
    color: 'text-violet-400',
    bg: 'bg-violet-950/50 border-violet-900/40',
    dot: 'bg-violet-400'
  },
  integration: {
    icon: Plug,
    color: 'text-blue-400',
    bg: 'bg-blue-950/50 border-blue-900/40',
    dot: 'bg-blue-400'
  },
  success: {
    icon: CheckCircle2,
    color: 'text-emerald-400',
    bg: 'bg-emerald-950/50 border-emerald-900/40',
    dot: 'bg-emerald-400'
  },
  warning: {
    icon: AlertCircle,
    color: 'text-amber-400',
    bg: 'bg-amber-950/50 border-amber-900/40',
    dot: 'bg-amber-400'
  },
  system: {
    icon: Info,
    color: 'text-slate-400',
    bg: 'bg-slate-950/50 border-slate-800/40',
    dot: 'bg-slate-400'
  }
};
function formatTime(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60000) return 'ahora';
  if (diff < 3600000) return `hace ${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return `hace ${Math.floor(diff / 3600000)}h`;
  return new Date(ts).toLocaleDateString('es', {
    day: 'numeric',
    month: 'short'
  });
}
interface ToastProps {
  notification: Notification;
  onDismiss: () => void;
}
const defaultCfg = {
  icon: Info,
  color: 'text-slate-400',
  bg: 'bg-slate-950/50 border-slate-900/40',
  dot: 'bg-slate-400'
};
function Toast({
  notification,
  onDismiss
}: ToastProps) {
  const cfg = typeConfig[notification.type] || defaultCfg;
  const Icon = cfg.icon;
  useEffect(() => {
    const t = setTimeout(onDismiss, notification.type === 'agent_interaction' ? 8000 : 5000);
    return () => clearTimeout(t);
  }, []);
  return <div className={`flex items-start gap-3 p-3.5 rounded-xl border shadow-2xl shadow-black/50 min-w-[300px] max-w-[380px] animate-in slide-in-from-right-10 fade-in duration-300 ${cfg.bg} backdrop-blur-sm`} data-id="element-2462">
      <div className={`p-1.5 rounded-lg bg-slate-900/50 shrink-0`} data-id="element-2463">
        <Icon size={14} className={cfg.color} data-id="element-2464" />
      </div>
      <div className="flex-1 min-w-0" data-id="element-2465">
        <p className="text-xs font-semibold text-slate-100" data-id="element-2466">
          {notification.title}
        </p>
        <p className="text-[11px] text-slate-400 mt-0.5 leading-relaxed" data-id="element-2467">
          {notification.message}
        </p>
        {notification.action && <button onClick={() => {
        notification.action!.onClick();
        onDismiss();
      }} className={`mt-2 text-[11px] font-semibold ${cfg.color} hover:underline`} data-id="element-2468">
            {notification.action.label} →
          </button>}
      </div>
      <button onClick={onDismiss} className="text-slate-600 hover:text-slate-400 transition-colors shrink-0 p-0.5" data-id="element-2469">
        <X size={12} data-id="element-2470" />
      </button>
    </div>;
}
// Toast container — rendered at top level
export function ToastContainer() {
  const {
    notifications
  } = useNotifications();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const prevIds = useRef<Set<string>>(new Set(notifications.map(n => n.id)));
  // Only show toasts for NEW notifications (not initial ones)
  const [toastQueue, setToastQueue] = useState<Notification[]>([]);
  useEffect(() => {
    const newOnes = notifications.filter(n => !prevIds.current.has(n.id) && !dismissed.has(n.id));
    if (newOnes.length > 0) {
      setToastQueue(prev => [...prev, ...newOnes]);
      newOnes.forEach(n => prevIds.current.add(n.id));
    }
  }, [notifications]);
  const dismiss = (id: string) => {
    setDismissed(prev => new Set([...prev, id]));
    setToastQueue(prev => prev.filter(n => n.id !== id));
  };
  if (toastQueue.length === 0) return null;
  return <div className="fixed bottom-6 right-6 z-[200] flex flex-col gap-2 items-end" data-id="element-2471">
      {toastQueue.slice(0, 3).map(n => <Toast key={n.id} notification={n} onDismiss={() => dismiss(n.id)} data-id="element-2472" />)}
    </div>;
}
export function NotificationCenter() {
  const {
    notifications,
    unreadCount,
    markRead,
    markAllRead,
    remove,
    clearAll
  } = useNotifications();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  const filtered = filter === 'unread' ? notifications.filter(n => !n.read) : notifications;
  return <div ref={ref} className="relative" data-id="element-2473">
      {/* Bell Button */}
      <button onClick={() => setOpen(v => !v)} className={`relative p-2 rounded-lg transition-colors ${open ? 'bg-slate-800 text-slate-200' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`} data-id="element-2474">
        <Bell size={18} data-id="element-2475" />
        {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-indigo-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" data-id="element-2476">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>}
      </button>

      {/* Panel */}
      {open && <div className="absolute right-0 top-full mt-2 w-96 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl shadow-black/60 z-[150] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-2477">
          {/* Header */}
          <div className="p-4 border-b border-slate-800 flex items-center justify-between" data-id="element-2478">
            <div className="flex items-center gap-2" data-id="element-2479">
              <Bell size={16} className="text-indigo-400" data-id="element-2480" />
              <h3 className="text-sm font-semibold text-slate-100" data-id="element-2481">
                Notificaciones
              </h3>
              {unreadCount > 0 && <span className="px-1.5 py-0.5 bg-indigo-600 text-white text-[10px] font-bold rounded-full" data-id="element-2482">
                  {unreadCount}
                </span>}
            </div>
            <div className="flex items-center gap-1" data-id="element-2483">
              <button onClick={markAllRead} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors" title="Marcar todas como leídas" data-id="element-2484">
                <CheckCheck size={14} data-id="element-2485" />
              </button>
              <button onClick={clearAll} className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-slate-800 transition-colors" title="Limpiar todo" data-id="element-2486">
                <Trash2 size={14} data-id="element-2487" />
              </button>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex border-b border-slate-800" data-id="element-2488">
            {[{
          key: 'all' as const,
          label: `Todas (${notifications.length})`
        }, {
          key: 'unread' as const,
          label: `No leídas (${unreadCount})`
        }].map(({
          key,
          label
        }) => <button key={key} onClick={() => setFilter(key)} className={`flex-1 py-2 text-xs font-medium transition-colors ${filter === key ? 'text-indigo-400 border-b-2 border-indigo-500' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-2489">
                {label}
              </button>)}
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto" data-id="element-2490">
            {filtered.length === 0 ? <div className="py-12 text-center text-slate-600" data-id="element-2491">
                <Bell size={32} className="mx-auto mb-3 opacity-30" data-id="element-2492" />
                <p className="text-sm" data-id="element-2493">Sin notificaciones</p>
              </div> : filtered.map(n => {
          const cfg = typeConfig[n.type] || defaultCfg;
          const Icon = cfg.icon;
          return <div key={n.id} onClick={() => markRead(n.id)} className={`flex items-start gap-3 p-4 border-b border-slate-800/50 cursor-pointer transition-colors hover:bg-slate-800/30 ${!n.read ? 'bg-slate-800/20' : ''}`} data-id="element-2494">
                    <div className={`p-1.5 rounded-lg shrink-0 mt-0.5 ${!n.read ? cfg.bg : 'bg-slate-800'}`} data-id="element-2495">
                      <Icon size={13} className={cfg.color} data-id="element-2496" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2497">
                      <div className="flex items-start justify-between gap-2" data-id="element-2498">
                        <p className={`text-xs font-semibold ${!n.read ? 'text-slate-100' : 'text-slate-400'}`} data-id="element-2499">
                          {n.title}
                        </p>
                        <div className="flex items-center gap-1.5 shrink-0" data-id="element-2500">
                          {!n.read && <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} data-id="element-2501"></span>}
                          <span className="text-[10px] text-slate-600 flex items-center gap-0.5" data-id="element-2502">
                            <Clock size={9} data-id="element-2503" /> {formatTime(n.timestamp)}
                          </span>
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed" data-id="element-2504">
                        {n.message}
                      </p>
                      {n.source && <span className="text-[10px] text-slate-600 mt-1 block" data-id="element-2505">
                          {n.source}
                        </span>}
                      {n.action && <button onClick={e => {
                e.stopPropagation();
                n.action!.onClick();
                setOpen(false);
              }} className={`mt-1.5 text-[11px] font-semibold ${cfg.color} hover:underline`} data-id="element-2506">
                          {n.action.label} →
                        </button>}
                    </div>
                    <button onClick={e => {
              e.stopPropagation();
              remove(n.id);
            }} className="p-0.5 text-slate-700 hover:text-slate-400 transition-colors shrink-0" data-id="element-2507">
                      <X size={12} data-id="element-2508" />
                    </button>
                  </div>;
        })}
          </div>
        </div>}
    </div>;
}